import { formatDistanceToNow } from 'date-fns';
import { Bot, Send } from 'lucide-react-native';
import { useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import type { Note } from '../../../types/leads';
import { agentTypeLabelKey } from '../../agents/utils/agentDisplayName';
import { CommentRow, GlyphAvatar } from './CommentRow';

const VOICE_CALL_HIGHLIGHTS_MARKER = '[[VOICE_CALL_HIGHLIGHTS]]';

interface NotesListProps {
  notes: Note[];
  onAddNote: (content: string) => void;
  loading?: boolean;
}

/**
 * The lead's internal comment thread.
 *
 * Two things were fixed here. The composer used to reserve a 96px bordered well
 * whether or not anyone was writing, so an empty thread was mostly empty box;
 * it now sits on one line and opens when it has your attention. And each note
 * used to be its own bordered slab inside an already-bordered card — a
 * conversation rendered as a filing cabinet. Notes now sit directly on the card
 * with an avatar gutter and vertical rhythm, the same anatomy the LinkedIn
 * comments use.
 */
export function NotesList({ notes, onAddNote, loading }: Readonly<NotesListProps>) {
  const { t, language } = useTranslation();
  const [newNote, setNewNote] = useState('');
  const [composing, setComposing] = useState(false);

  const hasDraft = newNote.trim().length > 0;
  const open = composing || hasDraft;

  const handleSubmit = () => {
    if (!hasDraft) return;
    onAddNote(newNote.trim());
    setNewNote('');
    setComposing(false);
  };

  return (
    <VStack spacing="xl">
      <VStack spacing="sm">
        <FormInput
          testID="lead_detail_note_input"
          value={newNote}
          onChangeText={setNewNote}
          onFocus={() => setComposing(true)}
          onBlur={() => setComposing(false)}
          placeholder={t('leads.notes.placeholder')}
          multiline={open}
          style={[styles.composer, open ? styles.composerOpen : styles.composerRest]}
          editable={!loading}
        />
        {open && (
          <HStack justify="end">
            <Button
              testID="lead_detail_note_submit_button"
              variant="primary"
              size="sm"
              pill
              onPress={handleSubmit}
              disabled={!hasDraft}
              loading={loading}
            >
              <HStack spacing="xs" align="center">
                <Send size={13} color={colors['primary-foreground']} strokeWidth={2} />
                <Text
                  variant="caption"
                  color={colors['primary-foreground']}
                  style={styles.submitLabel}
                >
                  {t('leads.notes.addNote')}
                </Text>
              </HStack>
            </Button>
          </HStack>
        )}
      </VStack>

      {notes.length === 0 ? (
        <Text variant="caption" color={colors.text.tertiary}>
          {t('leads.notes.noNotes')}
        </Text>
      ) : (
        <VStack spacing="lg">
          {notes.map((note) => {
            const isSystemNote = note.authorType === 'ai_agent' || note.authorType === 'system';
            const displayContent = note.content.startsWith(VOICE_CALL_HIGHLIGHTS_MARKER)
              ? note.content.slice(VOICE_CALL_HIGHLIGHTS_MARKER.length).trim()
              : note.content;
            const aiLabel = note.agentType
              ? `${t('leads.notes.aiAgent')} · ${t(agentTypeLabelKey(note.agentType))}`
              : t('leads.notes.aiAgent');
            const authorName = isSystemNote ? aiLabel : note.user.fullName;

            return (
              <CommentRow
                key={note.id}
                authorName={authorName}
                timestamp={formatDistanceToNow(new Date(note.createdAt), {
                  addSuffix: true,
                  locale: DATE_LOCALES[language],
                })}
                body={displayContent}
                avatarSlot={
                  isSystemNote ? (
                    <GlyphAvatar
                      glyph={<Bot size={14} color={colors.primary[300]} strokeWidth={2} />}
                    />
                  ) : undefined
                }
                // An agent note is structured output — mono earns its place on
                // the label/value lines it emits. A human note is prose.
                bodyStyle={
                  isSystemNote
                    ? { fontFamily: darkPrecisionFontFamilies.metric, lineHeight: 19 }
                    : undefined
                }
              />
            );
          })}
        </VStack>
      )}
    </VStack>
  );
}

const styles = StyleSheet.create({
  composer: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    backgroundColor: colors.background.input,
    borderWidth: 1,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        transitionProperty: 'min-height, border-color',
        transitionDuration: `${tokens.duration.fast}ms`,
        transitionTimingFunction: tokens.easing.default,
      } as object,
      default: {},
    }),
  },
  composerRest: {
    minHeight: 44,
    paddingVertical: tokens.spacing.md,
  },
  composerOpen: {
    minHeight: 88,
    paddingVertical: tokens.spacing.md,
    borderColor: colors.primary.border,
  },
  submitLabel: {
    fontWeight: '600',
  },
});
